import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRight, Copy, Check, Download, FilePlus2, Trash2, Save, Upload, ClipboardList,
} from 'lucide-react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useToast } from '../../components/Toast';
import {
  useSkillCategories, useSkills, useSkillFiles, useSaveFile, useDeleteFile,
  useUpsertSkill, useDeleteSkill, usePublishSkill,
  type SkillFile, type SkillStatus,
} from './skillsQueries';
import { makeZip } from './zip';
import styles from './skills.module.css';

const STATUSES: { value: SkillStatus; label: string }[] = [
  { value: 'draft', label: 'טיוטה' },
  { value: 'published', label: 'פורסם' },
  { value: 'archived', label: 'ארכיון' },
];

/** The paste-into-Claude-Code block: install instructions plus every file. */
function pastePrompt(skillSlug: string, files: SkillFile[]): string {
  const head = [
    `צור בפרויקט הזה את הסקיל \`${skillSlug}\` תחת \`.claude/skills/${skillSlug}/\`.`,
    'כל קובץ מופיע למטה עם הנתיב שלו יחסית לתיקיית הסקיל. צור אותם בדיוק כפי שהם.',
    'אל תשנה את התוכן ואל תוסיף קבצים שלא מופיעים כאן.',
    '',
  ].join('\n');
  const body = files
    .map((f) => `--- ${skillSlug}/${f.path} ---\n\n${f.content}\n`)
    .join('\n');
  return `${head}\n${body}`;
}

export function SkillEditor() {
  const { slug = '' } = useParams<{ slug: string }>();
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();

  const { data: skills = [] } = useSkills(orgId);
  const { data: categories = [] } = useSkillCategories(orgId);
  const skill = useMemo(() => skills.find((s) => s.slug === slug) ?? null, [skills, slug]);
  const skillId = skill?.id ?? null;

  const { data: files = [], isLoading: filesLoading } = useSkillFiles(skillId);
  const saveFile = useSaveFile(orgId, skillId);
  const deleteFile = useDeleteFile(orgId, skillId);
  const upsertSkill = useUpsertSkill(orgId);
  const deleteSkill = useDeleteSkill(orgId);
  const publish = usePublishSkill(orgId, userId);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftPath, setDraftPath] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  const selected = useMemo(
    () => files.find((f) => f.id === selectedId) ?? null,
    [files, selectedId],
  );

  // Select the first file once, and follow the selection into the editor.
  useEffect(() => {
    if (!selectedId && files.length > 0) setSelectedId(files[0]?.id ?? null);
  }, [files, selectedId]);

  useEffect(() => {
    setDraftPath(selected?.path ?? '');
    setDraftBody(selected?.content ?? '');
  }, [selected]);

  const dirty = selected !== null && (draftPath !== selected.path || draftBody !== selected.content);

  async function copyText(id: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      window.setTimeout(() => { setCopied((c) => (c === id ? null : c)); }, 1800);
    } catch {
      toast.error('ההעתקה נכשלה');
    }
  }

  function downloadZip() {
    if (!skill) return;
    const blob = makeZip(files.map((f) => ({ path: `${skill.slug}/${f.path}`, content: f.content })));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${skill.slug}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function onSave() {
    if (!selected) return;
    try {
      await saveFile.mutateAsync({ id: selected.id, path: draftPath, content: draftBody });
      toast.success('הקובץ נשמר');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'השמירה נכשלה');
    }
  }

  async function onAddFile() {
    const path = window.prompt('נתיב הקובץ, יחסית לתיקיית הסקיל', 'SKILL.md');
    if (!path?.trim()) return;
    try {
      await saveFile.mutateAsync({ id: null, path, content: '' });
      toast.success('הקובץ נוסף');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'הוספת הקובץ נכשלה');
    }
  }

  async function onDeleteFile(file: SkillFile) {
    if (!window.confirm(`למחוק את ${file.path}? המחיקה סופית, אלא אם פרסמת גרסה קודם.`)) return;
    try {
      await deleteFile.mutateAsync(file.id);
      if (selectedId === file.id) setSelectedId(null);
      toast.success('הקובץ נמחק');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'המחיקה נכשלה');
    }
  }

  async function onPublish() {
    if (!skill) return;
    const version = window.prompt('מספר גרסה לפרסום', skill.version);
    if (!version?.trim()) return;
    const note = window.prompt('מה השתנה בגרסה הזאת?') ?? null;
    try {
      await publish.mutateAsync({ skillId: skill.id, version: version.trim(), note, files });
      toast.success(`גרסה ${version.trim()} פורסמה`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'הפרסום נכשל');
    }
  }

  async function onMeta(patch: Partial<{ name: string; description: string | null; category_id: string | null; status: SkillStatus }>) {
    if (!skill) return;
    try {
      await upsertSkill.mutateAsync({
        id: skill.id,
        values: {
          category_id: patch.category_id !== undefined ? patch.category_id : skill.category_id,
          slug: skill.slug,
          name: patch.name ?? skill.name,
          description: patch.description !== undefined ? patch.description : skill.description,
          status: patch.status ?? skill.status,
          version: skill.version,
          tags: skill.tags,
        },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'העדכון נכשל');
    }
  }

  async function onDeleteSkill() {
    if (!skill) return;
    if (!window.confirm(`למחוק את הסקיל "${skill.name}" על כל קבציו?`)) return;
    try {
      await deleteSkill.mutateAsync(skill.id);
      toast.success('הסקיל נמחק');
      window.location.assign('/skills');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'המחיקה נכשלה');
    }
  }

  if (!skill) {
    return (
      <div className={styles.page}>
        <p className={styles.muted}>הסקיל לא נמצא.</p>
        <Link to="/skills" className={styles.btnSecondary}>
          <ArrowRight size={15} aria-hidden="true" />
          חזרה לספרייה
        </Link>
      </div>
    );
  }

  const totalBytes = files.reduce((sum, f) => sum + (f.bytes ?? 0), 0);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <Link to="/skills" className={styles.back}>
            <ArrowRight size={14} aria-hidden="true" />
            הספרייה
          </Link>
          <h1>{skill.name}</h1>
          <p className={styles.sub}>
            <span className={styles.path} dir="ltr">{skill.slug}</span>
            {' · '}
            {files.length} קבצים · {(totalBytes / 1024).toFixed(1)} KB · v{skill.version}
          </p>
        </div>
      </header>

      <div className={styles.metaBar}>
        <label>
          נושא
          <select
            value={skill.category_id ?? ''}
            onChange={(e) => void onMeta({ category_id: e.target.value || null })}
          >
            <option value="">ללא נושא</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
        <label>
          סטטוס
          <select
            value={skill.status}
            onChange={(e) => void onMeta({ status: e.target.value as SkillStatus })}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>
        <div className={styles.metaActions}>
          <button type="button" className={styles.btnSecondary} onClick={() => void copyText('prompt', pastePrompt(skill.slug, files))}>
            {copied === 'prompt' ? <Check size={15} aria-hidden="true" /> : <ClipboardList size={15} aria-hidden="true" />}
            {copied === 'prompt' ? 'הועתק' : 'העתק כפרומפט'}
          </button>
          <button type="button" className={styles.btnSecondary} onClick={downloadZip}>
            <Download size={15} aria-hidden="true" />
            הורד ZIP
          </button>
          <button type="button" className={styles.btnSecondary} onClick={() => void onPublish()}>
            <Upload size={15} aria-hidden="true" />
            פרסם גרסה
          </button>
          <button type="button" className={styles.btnDanger} onClick={() => void onDeleteSkill()}>
            <Trash2 size={15} aria-hidden="true" />
            מחק סקיל
          </button>
        </div>
      </div>

      <div className={styles.editorLayout}>
        <aside className={styles.fileList} aria-label="קבצים">
          <div className={styles.fileListHead}>
            <span>קבצים</span>
            <button type="button" className={styles.iconBtn} onClick={() => void onAddFile()} aria-label="הוסף קובץ">
              <FilePlus2 size={15} aria-hidden="true" />
            </button>
          </div>
          {filesLoading && <p className={styles.muted}>טוען…</p>}
          {!filesLoading && files.length === 0 && (
            <p className={styles.muted}>אין קבצים. התחל מ-SKILL.md.</p>
          )}
          <ul>
            {files.map((file) => (
              <li key={file.id} className={file.id === selectedId ? styles.fileRowActive : styles.fileRow}>
                <button type="button" onClick={() => { setSelectedId(file.id); }} className={styles.fileName}>
                  <span className={styles.path} dir="ltr">{file.path}</span>
                </button>
                <button type="button" className={styles.iconBtn} onClick={() => void copyText(file.id, file.content)} aria-label={`העתק ${file.path}`}>
                  {copied === file.id ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                </button>
                <button type="button" className={styles.iconBtn} onClick={() => void onDeleteFile(file)} aria-label={`מחק ${file.path}`}>
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className={styles.editor}>
          {!selected && <p className={styles.muted}>בחר קובץ מהרשימה.</p>}
          {selected && (
            <>
              <div className={styles.editorHead}>
                <input
                  type="text"
                  value={draftPath}
                  onChange={(e) => { setDraftPath(e.target.value); }}
                  dir="ltr"
                  aria-label="נתיב הקובץ"
                  className={styles.pathInput}
                />
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={() => void onSave()}
                  disabled={!dirty || saveFile.isPending}
                >
                  <Save size={15} aria-hidden="true" />
                  {saveFile.isPending ? 'שומר…' : dirty ? 'שמור' : 'נשמר'}
                </button>
              </div>
              <textarea
                value={draftBody}
                onChange={(e) => { setDraftBody(e.target.value); }}
                spellCheck={false}
                className={styles.textarea}
                aria-label="תוכן הקובץ"
              />
            </>
          )}
        </section>
      </div>
    </div>
  );
}
